import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";

// Cozy admin building blocks — cream/terracotta, Fredoka headings, clay cards.

export const Card = ({ className = "", children, ...rest }) => (
    <div
        className={`bg-cream rounded-clay shadow-clay border border-warm-300/30 ${className}`}
        {...rest}
    >
        {children}
    </div>
);

export const SectionTitle = ({ children, hint }) => (
    <div className="mb-4">
        <h2 className="font-display text-warm-900 text-xl">{children}</h2>
        {hint && <p className="text-warm-500 text-sm mt-1">{hint}</p>}
    </div>
);

const STATUS_STYLES = {
    active: "bg-sage/25 text-warm-900 border-sage/40",
    used: "bg-sage/25 text-warm-900 border-sage/40",
    pending: "bg-mustard-soft text-warm-900 border-mustard/40",
    expired: "bg-surface text-warm-500 border-warm-300/50",
    full: "bg-mustard-soft text-warm-900 border-mustard/40",
    revoked: "bg-terracotta-soft/60 text-warm-900 border-terracotta/40",
};

export const StatusPill = ({ status }) => (
    <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.expired}`}
        data-testid={`status-pill-${status}`}
    >
        {status}
    </span>
);

export const StatCard = ({ label, value, accent = "terracotta", sub, testid }) => {
    const ring = {
        terracotta: "text-terracotta",
        sage: "text-sage",
        mustard: "text-mustard",
    }[accent];
    return (
        <Card className="p-5 flex flex-col justify-between min-h-[120px]" data-testid={testid}>
            <span className="text-warm-500 text-xs uppercase tracking-wide font-semibold">
                {label}
            </span>
            <div className="mt-2 flex items-baseline gap-1">
                <span className={`font-display text-4xl ${ring}`}>{value}</span>
            </div>
            {sub && <span className="text-warm-500 text-xs mt-1">{sub}</span>}
        </Card>
    );
};

export const EmptyState = ({ children, testid }) => (
    <div
        className="text-center py-10 text-warm-500 text-sm"
        data-testid={testid}
    >
        {children}
    </div>
);

export const Spinner = ({ label = "loading…" }) => (
    <div className="flex items-center justify-center gap-2 py-10 text-warm-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>{label}</span>
    </div>
);

// Lightweight kawaii modal (self-contained, no portal quirks).
export const KawaiiModal = ({ open, onClose, title, children, testid }) => (
    <AnimatePresence>
        {open && (
            <motion.div
                className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                data-testid={testid}
            >
                <div
                    className="absolute inset-0 bg-warm-900/30 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid={testid ? `${testid}-backdrop` : undefined}
                />
                <motion.div
                    className="relative bg-cream rounded-clay shadow-float border border-warm-300/40 w-full max-w-md p-6"
                    initial={{ scale: 0.94, y: 12, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.96, y: 8, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 22 }}
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-warm-900 text-lg">{title}</h3>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-full hover:bg-surface transition-colors"
                            aria-label="Close"
                            data-testid={testid ? `${testid}-close` : undefined}
                        >
                            <X className="w-4 h-4 text-warm-500" />
                        </button>
                    </div>
                    {children}
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, confirmLabel = "Yes, do it", danger = false, testid }) => (
    <KawaiiModal open={open} onClose={onClose} title={title} testid={testid}>
        <p className="text-warm-800 text-sm leading-relaxed mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
            <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-semibold text-warm-800 bg-surface hover:bg-surface-hover transition-colors"
                data-testid={testid ? `${testid}-cancel` : undefined}
            >
                Never mind
            </button>
            <button
                onClick={onConfirm}
                className={`px-4 py-2 rounded-full text-sm font-semibold text-cream transition-colors ${danger ? "bg-terracotta hover:bg-terracotta-hover" : "bg-sage hover:bg-sage-hover"}`}
                data-testid={testid ? `${testid}-confirm` : undefined}
            >
                {confirmLabel}
            </button>
        </div>
    </KawaiiModal>
);

// Kawaii table shell — soft rows, no harsh borders.
export const Table = ({ head, children, testid }) => (
    <div className="overflow-x-auto rounded-2xl border border-warm-300/30" data-testid={testid}>
        <table className="w-full text-left text-sm">
            <thead>
                <tr className="bg-surface/60 text-warm-500 text-xs uppercase tracking-wide">
                    {head.map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-warm-300/20">{children}</tbody>
        </table>
    </div>
);
